import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Eye,
  Pencil,
  Copy,
  Trash2,
  Printer,
  Search,
  ClipboardList,
  Plus,
} from "lucide-react";
import Layout from "@/components/Layout";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import GuidedTour from "@/components/GuidedTour";
import { SopStatsChart } from "@/components/SopStatsChart";
import { SopStatsTable } from "@/components/SopStatsTable";
import { canManageSop } from "@/lib/permissions";
import { consumePendingTour } from "@/lib/tour";
import { TOUR_STEPS } from "@/lib/tourSteps";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const [sops, setSops] = useState([]);
  const [reference, setReference] = useState({ sites: [], services: [] });
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [site, setSite] = useState("all");
  const [service, setService] = useState("all");
  const [year, setYear] = useState("all");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    if (consumePendingTour("national_admin")) setRunTour(true);
  }, []);

  const loadSops = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (site !== "all") params.site = site;
      if (service !== "all") params.service = service;
      if (year !== "all") params.year = year;
      const res = await api.get("/sops", { params });
      setSops(res.data);
    } catch (e) {
      toast.error("Impossible de charger les SOP. Vérifiez la configuration Google Sheets.");
    } finally {
      setLoading(false);
    }
  }, [q, site, service, year]);

  const canSeeStats = perms.role === "national_admin" || perms.role === "site_admin";
  const yearOptions = stats?.years?.length
    ? stats.years
    : Array.from({ length: new Date().getFullYear() - 2019 + 1 }, (_, i) => String(2019 + i)).reverse();

  useEffect(() => {
    api
      .get("/reference/all")
      .then((res) => setReference(res.data))
      .catch(() => {});
    if (canSeeStats) {
      api
        .get("/sops/stats")
        .then((res) => setStats(res.data))
        .catch(() => {});
    }
  }, [canSeeStats]);

  useEffect(() => {
    const t = setTimeout(loadSops, 300);
    return () => clearTimeout(t);
  }, [loadSops]);

  const handleStatsCellClick = (clickedSite, clickedYear) => {
    setSite(clickedSite);
    setYear(clickedYear);
    tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };


  const handleDuplicate = async (id) => {
    try {
      await api.post(`/sops/${id}/duplicate`);
      toast.success("SOP dupliquée avec succès");
      loadSops();
    } catch (e) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/sops/${toDelete}`);
      toast.success("SOP supprimée");
      setToDelete(null);
      loadSops();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground">Tableau de bord SOP</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Modes opératoires de sécurité — création, consultation, impression
            </p>
          </div>
          {perms.can_create && (
            <Button onClick={() => navigate("/sops/new")} data-testid="dashboard-new-sop-button">
              <Plus className="h-4 w-4" />
              Nouvelle SOP
            </Button>
          )}
        </div>

        {canSeeStats && (
          <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="sop-stats-section">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-1">
              SOP réalisés par site et par année
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Cliquez sur une cellule ou une barre pour filtrer la liste ci-dessous
            </p>
            {!stats && <p className="text-sm text-muted-foreground py-6 text-center">Chargement des statistiques...</p>}
            {stats && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SopStatsChart stats={stats} />
                <SopStatsTable stats={stats} onCellClick={handleStatsCellClick} />
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-5 flex items-center gap-4">
            <div className="h-11 w-11 rounded-lg bg-accent flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-accent-foreground" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground" data-testid="total-sops-count">
                {sops.length}
              </p>
              <p className="text-xs text-muted-foreground">SOP au total (filtrées)</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par titre ou description..."
              className="pl-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-keyword-input"
            />
          </div>
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-site-select">
              <SelectValue placeholder="Site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sites</SelectItem>
              {(perms.can_view_all_sites === false ? perms.sites : reference.sites)?.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-service-select">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les services</SelectItem>
              {reference.services?.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-year-select">
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les années</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div ref={tableRef} className="bg-card border border-border rounded-lg overflow-hidden">
          <Table data-testid="sops-table">
            <TableHeader>
              <TableRow>
                <TableHead>N° SOP</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              )}
              {!loading && sops.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8" data-testid="no-sops-message">
                    Aucune SOP trouvée. Créez votre première SOP.
                  </TableCell>
                </TableRow>
              )}
              {sops.map((sop) => {
                const canManage = canManageSop(user, sop);
                return (
                <TableRow key={sop.id} data-testid={`sop-row-${sop.id}`}>
                  <TableCell className="font-mono text-xs">{sop.numero_sop}</TableCell>
                  <TableCell className="font-medium">{sop.titre}</TableCell>
                  <TableCell>{sop.site}</TableCell>
                  <TableCell>{sop.service}</TableCell>
                  <TableCell>{sop.date}</TableCell>
                  <TableCell>
                    <Badge variant={sop.statut === "Actif" ? "default" : "secondary"}>{sop.statut}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Voir"
                        onClick={() => navigate(`/sops/${sop.id}`)}
                        data-testid={`view-sop-button-${sop.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Modifier"
                          onClick={() => navigate(`/sops/${sop.id}/edit`)}
                          data-testid={`edit-sop-button-${sop.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {perms.can_duplicate && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Dupliquer"
                          onClick={() => handleDuplicate(sop.id)}
                          data-testid={`duplicate-sop-button-${sop.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Imprimer / PDF"
                        onClick={() => navigate(`/sops/${sop.id}`)}
                        data-testid={`print-sop-button-${sop.id}`}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Supprimer"
                          onClick={() => setToDelete(sop.id)}
                          data-testid={`delete-sop-button-${sop.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <DeleteConfirmDialog
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer cette SOP ?"
        description="Cette SOP sera définitivement supprimée de Google Sheets. Cette action est irréversible."
      />
      <GuidedTour steps={TOUR_STEPS.national_admin} run={runTour} onFinish={() => setRunTour(false)} />
    </Layout>
  );
}
